// slices/accountInfo/ContractConfig.ts
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiNetworkProvider, getFirstSnapshotEpoch } from 'helpers/api';
import { FarmParameters, getFarmParameters } from 'helpers/api/farm';
import { getLatestBlockInfo } from 'helpers/api/mvx/getLatestBlockInfo';
import { getLastIssuanceEpoch } from 'helpers/api/rawVesta/getLastIssuanceEpoch';
import { getPoolAddresses } from 'helpers/api/rawVesta/getPoolAddresses';
import { getSnapshot, SnapshotEntry } from 'helpers/api/rawVesta/getSnapshot';

interface ContractConfig {
  snapshotStartingEpoch: number;
  lastIssuanceEpoch: number;
  currentEpoch: number;
  farmAddresses: string[];
  snapshots: { [key: string]: SnapshotEntry[] };
  parameters: { [key: string]: FarmParameters };
}

interface ContractConfigState {
  // your state fields
  data: ContractConfig | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ContractConfigState = {
  // initialize fields
  data: null,
  status: 'loading',
  error: null
};

export const fetchContractConfig = createAsyncThunk(
  'rawVestaMinterContractInfo/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const startingEpoch = await getFirstSnapshotEpoch();
      const lastIssuanceEpoch = await getLastIssuanceEpoch();
      const blockInfo = await getLatestBlockInfo();
      const farmAddresses = await getPoolAddresses();

      const snapshotObj: { [key: string]: SnapshotEntry[] } = {};
      const paramsObj: { [key: string]: FarmParameters } = {};
      for (let i = 0; i < farmAddresses.length; i++) {
        const farmSnapshot = await getSnapshot(farmAddresses[i]);
        const farmParameters = await getFarmParameters(farmAddresses[i]);
        snapshotObj[farmAddresses[i]] = farmSnapshot;
        if (farmParameters === null) {
          continue;
        }
        paramsObj[farmAddresses[i]] = farmParameters;
      }

      const configObj: ContractConfig = {
        snapshotStartingEpoch: startingEpoch,
        lastIssuanceEpoch: lastIssuanceEpoch,
        currentEpoch: blockInfo?.epoch ?? 0,
        farmAddresses: farmAddresses,
        snapshots: snapshotObj,
        parameters: paramsObj
      };
      console.log('raw vesta config', configObj);
      return configObj;
    } catch (err) {
      console.log(err);
      return rejectWithValue(err.message);
    }
  }
);

const ContractConfigSlice = createSlice({
  name: 'rawVestaMinterContractInfo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchContractConfig.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        fetchContractConfig.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.status = 'succeeded';
          state.data = action.payload;
        }
      )
      .addCase(
        fetchContractConfig.rejected,
        (state, action: PayloadAction<any>) => {
          state.status = 'failed';
          state.error = action.payload;
        }
      );
  }
});

export default ContractConfigSlice.reducer;
